import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GameTabs } from "@/components/GameTabs";
import { MatchCard } from "@/components/MatchCard";
import { useState, useMemo } from "react";
import { Trophy, Flame } from "lucide-react";
import LaunchpadMarketCardSkeleton from "@/components/ui/LaunchpadMarketCardSkeleton";
import { usePresales } from "@/hooks/usePresales";
import { transformPresaleToLaunchpad } from "@/lib/marketHelpers";
import { getGameFromTitle } from "@/lib/gameCategories";

const Index = () => {
  const [activeTab, setActiveTab] = useState("active");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGame, setSelectedGame] = useState("all");

  // Fetch data from subgraph
  const { data: presalesData, isLoading } = usePresales();

  // Convert presales to match-style format
  const matches = useMemo(() => {
    if (!presalesData) return [];
    
    const hiddenMarketIds = [
      '0xf50627a76fff393d6f4acaa7735325b666c9191eff94ac0eeb5939cbbd5fab94',
      '0xf3653e2e6c5c30b6ba7e1536146f5e0fd620d2440eacb55cd69426e9990c8b24'
    ];
    
    return presalesData
      .filter(presale => !hiddenMarketIds.includes(presale.id))
      .map(presale => {
        // Extract game info from title
        const gameFromTitle = getGameFromTitle(presale.question);
        
        return {
          id: presale.id,
          status: presale.resolved ? "ended" as const : 
                  presale.tokensCreated ? "live" as const : 
                  "upcoming" as const,
          category: "MARKET",
          gameCategory: gameFromTitle?.id || null, // Store the actual game ID for filtering (null if no code in title)
          question: presale.question,
          time: presale.tokensCreated ? 
                new Date(parseInt(presale.graduatedAt || "0") * 1000).toLocaleTimeString() : 
                new Date(parseInt(presale.deadline) * 1000).toLocaleDateString(),
          teamA: {
            name: "YES",
            score: presale.resolved ? (presale.forceResolvedAnswer === 1 ? 1 : 0) : undefined,
            odds: `${Math.round(Number(presale.yesOdds) || 0)}%`
          },
          teamB: {
            name: "NO",
            score: presale.resolved ? (presale.forceResolvedAnswer === 0 ? 1 : 0) : undefined,
            odds: `${Math.round(Number(presale.noOdds) || 0)}%`
          },
          bestOf: presale.question
        };
      });
  }, [presalesData]);

  // Filter by search and game category
  const filteredMatches = useMemo(() => {
    let filtered = [...matches]; // Create a new array to avoid mutation
    
    // Filter by game category
    if (selectedGame && selectedGame !== "all") {
      filtered = filtered.filter(match => {
        // If the game category is explicitly set in the title, match it
        if (match.gameCategory) {
          return match.gameCategory === selectedGame;
        }
        // If no game code in title and filtering for "other", include it
        if (selectedGame === "other") {
          return true;
        }
        // Otherwise exclude markets without game codes when filtering
        return false;
      });
    }
    
    // Filter by search query
    if (searchQuery && searchQuery.trim()) {
      filtered = filtered.filter(match => 
        match.question?.toLowerCase().includes(searchQuery.toLowerCase().trim())
      );
    }
    
    return filtered;
  }, [matches, searchQuery, selectedGame]);

  // Separate active and launchpad - memoize to ensure proper filtering
  const activeMatches = useMemo(() => 
    filteredMatches.filter(m => m.status === "live" || m.status === "ended"),
    [filteredMatches]
  );
  const launchpadMatches = useMemo(() => 
    filteredMatches.filter(m => m.status === "upcoming"),
    [filteredMatches]
  );

  return (
    <div className="min-h-screen bg-background">
      <Header searchQuery={searchQuery} onSearchChange={setSearchQuery} />
      <main className="pt-20 pb-12">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Main Tabs */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <button
              onClick={() => setActiveTab("active")}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                activeTab === "active"
                  ? "bg-primary text-background"
                  : "bg-card text-muted-foreground hover:text-foreground"
              }`}
            >
              <Trophy className="h-5 w-5" />
              Active Markets
            </button>
            <button
              onClick={() => setActiveTab("launchpad")}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                activeTab === "launchpad"
                  ? "bg-primary text-background"
                  : "bg-card text-muted-foreground hover:text-foreground"
              }`}
            >
              <Flame className="h-5 w-5" />
              Launchpad
            </button>
          </div>

          {/* Game Tabs */}
          <div className="mb-8">
            <GameTabs activeGame={selectedGame} onGameChange={setSelectedGame} />
          </div>

          {/* Match List */}
          <div className="space-y-3" key={`${activeTab}-${selectedGame}`}>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <LaunchpadMarketCardSkeleton key={index} />
              ))
            ) : activeTab === "active" ? (
              activeMatches.length === 0 ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <Trophy className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <p className="text-xl text-muted-foreground">No active markets found</p>
                    <p className="text-sm text-muted-foreground mt-2">Markets will appear here once launched</p>
                  </div>
                </div>
              ) : (
                activeMatches.map((match) => (
                  <MatchCard key={match.id} {...match} />
                ))
              )
            ) : (
              launchpadMatches.length === 0 ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <Flame className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <p className="text-xl text-muted-foreground">No presales in progress</p>
                    <p className="text-sm text-muted-foreground mt-2">Be the first to create a market!</p>
                  </div>
                </div>
              ) : (
                launchpadMatches.map((match) => (
                  <MatchCard key={match.id} {...match} />
                ))
              )
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Index;
